import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { Input } from '@components/Input';
import { Select } from '@components/Select';
import { MultiSelect } from '@components/MultiSelect';
import { useTranslation } from '@hooks';
import {
    SPICINESS_LEVELS,
    ALLERGENS,
    DIETARY_RESTRICTIONS,
    PREPARATION_METHODS,
    NUTRITION_UNIT_DEFAULTS,
} from '@constants/menus';
import { getLocalizedValue, setLocalizedValue } from '../helpers';

const Grid = styled('div')({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gridGap: 12,
    marginTop: 8,
});

const NUTRITION_FIELDS = ['totalFat', 'cholesterol', 'sodium', 'totalCarbohydrate', 'protein'];

const ItemAttributes = ({ isView, attributes, defaultLanguage, onChange }) => {
    const { t } = useTranslation();

    const attrs = attributes || {};

    const update = patch => onChange({ ...attrs, ...patch });

    const spicinessOptions = useMemo(
        () => [
            { value: '', label: t('common.none') },
            ...SPICINESS_LEVELS.map(value => ({ value, label: t(`MenusPage.spicinessValues.${value}`) })),
        ],
        [t],
    );
    const allergenOptions = useMemo(
        () => ALLERGENS.map(value => ({ value, label: t(`MenusPage.allergenValues.${value}`) })),
        [t],
    );
    const dietaryOptions = useMemo(
        () => DIETARY_RESTRICTIONS.map(value => ({ value, label: t(`MenusPage.dietaryValues.${value}`) })),
        [t],
    );
    const preparationOptions = useMemo(
        () => PREPARATION_METHODS.map(value => ({ value, label: t(`MenusPage.preparationValues.${value}`) })),
        [t],
    );

    const toInt = value => (value === '' ? null : Math.trunc(Number(value)));

    const onChangePortion = patch => {
        const next = { ...(attrs.portionSize || {}), ...patch };
        const isEmpty = next.quantity == null && !(next.unit || []).some(u => u.value?.trim());
        update({ portionSize: isEmpty ? null : next });
    };

    const onChangeNutrition = (field, value) => {
        const facts = { ...(attrs.nutritionFacts || {}) };
        if (value === '') {
            facts[field] = null;
        } else if (field === 'calories') {
            facts[field] = { amount: toInt(value), unit: 'CALORIE' };
        } else {
            facts[field] = { amount: Number(value), unit: NUTRITION_UNIT_DEFAULTS[field] };
        }
        update({ nutritionFacts: facts });
    };

    const ingredients = getLocalizedValue(attrs.ingredients, defaultLanguage);

    return (
        <Grid>
            <Select
                value={attrs.spiciness || ''}
                onChange={e => update({ spiciness: e.target.value || null })}
                label={t('MenusPage.spiciness')}
                fullWidth
                options={spicinessOptions}
                disabled={isView}
            />
            <MultiSelect
                value={attrs.allergens || []}
                onChange={selected => update({ allergens: selected })}
                options={allergenOptions}
                label={t('MenusPage.allergens')}
                countable
                disabled={isView}
            />
            <MultiSelect
                value={attrs.dietaryRestrictions || []}
                onChange={selected => update({ dietaryRestrictions: selected })}
                options={dietaryOptions}
                label={t('MenusPage.dietaryRestrictions')}
                countable
                disabled={isView}
            />
            <MultiSelect
                value={attrs.preparationMethods || []}
                onChange={selected => update({ preparationMethods: selected })}
                options={preparationOptions}
                label={t('MenusPage.preparationMethods')}
                countable
                disabled={isView}
            />
            <Input
                inputProps={{
                    value: ingredients,
                    onChange: e =>
                        update({
                            ingredients: setLocalizedValue(attrs.ingredients, defaultLanguage, e.target.value),
                        }),
                }}
                label={t('MenusPage.ingredients')}
                fullWidth
                disabled={isView}
            />
            <Input
                type="number"
                inputProps={{
                    value: attrs.servesNumPeople ?? '',
                    onChange: e => update({ servesNumPeople: toInt(e.target.value) }),
                    min: 1,
                    step: 1,
                }}
                label={t('MenusPage.servesNumPeople')}
                fullWidth
                disabled={isView}
            />
            <Input
                type="number"
                inputProps={{
                    value: attrs.portionSize?.quantity ?? '',
                    onChange: e => onChangePortion({ quantity: toInt(e.target.value) }),
                    min: 1,
                    step: 1,
                }}
                label={t('MenusPage.portionQuantity')}
                fullWidth
                disabled={isView}
            />
            <Input
                inputProps={{
                    value: getLocalizedValue(attrs.portionSize?.unit, defaultLanguage),
                    onChange: e =>
                        onChangePortion({
                            unit: setLocalizedValue(attrs.portionSize?.unit, defaultLanguage, e.target.value),
                        }),
                }}
                label={t('MenusPage.portionUnit')}
                fullWidth
                disabled={isView}
            />
            <Input
                type="number"
                inputProps={{
                    value: attrs.nutritionFacts?.calories?.amount ?? '',
                    onChange: e => onChangeNutrition('calories', e.target.value),
                    min: 0,
                    step: 1,
                }}
                label={t('MenusPage.calories')}
                fullWidth
                disabled={isView}
            />
            {NUTRITION_FIELDS.map(field => (
                <Input
                    key={field}
                    type="number"
                    inputProps={{
                        value: attrs.nutritionFacts?.[field]?.amount ?? '',
                        onChange: e => onChangeNutrition(field, e.target.value),
                        min: 0,
                    }}
                    addonComponent={t(`MenusPage.units.${NUTRITION_UNIT_DEFAULTS[field]}`)}
                    addonWithoutWrapper
                    label={t(`MenusPage.nutrition.${field}`)}
                    fullWidth
                    disabled={isView}
                />
            ))}
        </Grid>
    );
};

export default ItemAttributes;
